import { CONTEXT_LENGTH } from '../store/schema/CachedSuggestion';
import getStore from '../store/store';
import getLastNCharacters from './getLastNCharacters';
import { CachedSuggestion } from '../store/schema/CachedSuggestion';

/**
Check the cache to see if we already have a suggestion saved that matches the current context
 */
export default function getSuggestionFromCache(charactersBeforeCursor: string): CachedSuggestion {
    // Trim the characters before cursor so we have at most the last 'CONTEXT_LENGTH' number of characters
    const charactersToLookFor = getLastNCharacters(charactersBeforeCursor, CONTEXT_LENGTH);

    const store = getStore();
    const { suggestionCache } = store;

    // Now go through all of the suggestions in the cache and see if any of them match the context
    for (let i = 0; i < suggestionCache.length; i++) {
        const entry = suggestionCache[i];

        // The full suggestion is the context of the suggestion plus the suggestion string. We need to combine them before searching
        // because in the case of backspacing through a suggestion the current context may contain more of the characters than the original
        // context did when the suggestion was fetched.
        // For example:
        //      At the time the suggestion was retrieved:
        //          Context: "I am loo"
        //          Suggestion: "king forward"
        //      Now, the user has typed some more and started backspacing, so the context is:
        //          Context: "I am looki"
        //      We should return the cached value:
        //          Suggestion: "ng forward"
        //      But the only way that "I am looki" will be found is if we combine the cached context and suggestion
        const fullSuggestion = entry.context + entry.suggestion;

        // Find the location of the current characters within the full suggestion
        const positionOfCharactersToLookFor = fullSuggestion.lastIndexOf(charactersToLookFor);

        // If we find the current characters in the full suggestion, then snip off the difference from the end, and that
        // will be the suggestion we want to return from the cache.
        // Continuing the above example:
        //      charactersToLookFor: "I am looki"
        //      fullSuggestion: "I am looking forward"
        //      positionOfCharactersToLookFor: 0
        //      snip: "ng forward"
        if (positionOfCharactersToLookFor !== -1) {
            const snip = fullSuggestion.substring(
                positionOfCharactersToLookFor + charactersToLookFor.length
            );

            // If the snip is less than the cutoff length then the suggestion is only valid if it's a complete match
            // This prevents us from returning a meaninglessly small suggestion when we don't have enough context.
            // Example:
            //      Cached values:
            //          Context: "I am lo"
            //          Suggestion: "oking forward"
            //      Current context:
            //          charactersToLookFor: "k"
            //      At this point, snip will be "ing forward" because the "k" matches inside of the cached value, but that
            //      is a meaningless suggestion to render. To guard against this we're counting on two factors:
            //          - If the charactersBeforeCursor at least CONTEXT_LENGTH (e.g. 50 characters) then the chance of a mistake like the one above
            //            is pretty unlikely
            //          - If the charactersBeforeCursor is less than CONTEXT_LENGTH, but it matches the cached context exactly then we're okay
            if (
                charactersBeforeCursor.length < CONTEXT_LENGTH &&
                positionOfCharactersToLookFor !== 0
            ) {
                break;
            }

            // We don't want to suggest the user's own content back to them, so never suggest a snip that is longer than the suggestion
            // that we received from the service.
            if (snip?.length <= entry.suggestion.length) {
                // Return a modified cached suggestion using the telemetry and context from the original,
                // but with the modified suggestion
                return {
                    context: entry.context,
                    suggestion: snip,
                    suggestionTelemetry: entry.suggestionTelemetry,
                };
            }
        }
    }

    return null;
}
